import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, Calendar, Users, DollarSign,
  RefreshCw, Loader2, AlertTriangle, CheckCircle2, Zap, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_META = {
  ventas:  { label: 'Agente de Ventas',  icon: TrendingUp, color: 'text-blue-400',    bg: 'bg-blue-500/8',    border: 'border-blue-500/20'   },
  agenda:  { label: 'Agente de Agenda',  icon: Calendar,   color: 'text-violet-400',  bg: 'bg-violet-500/8',  border: 'border-violet-500/20' },
  crm:     { label: 'Agente de CRM',     icon: Users,      color: 'text-emerald-400', bg: 'bg-emerald-500/8', border: 'border-emerald-500/20'},
  cobros:  { label: 'Agente de Cobros',  icon: DollarSign, color: 'text-amber-400',   bg: 'bg-amber-500/8',   border: 'border-amber-500/20'  },
};

const RECENT_ICONS = {
  ventas:  TrendingUp,
  agenda:  Calendar,
  crm:     Users,
  cobros:  DollarSign,
};

const RECENT_COLORS = {
  ventas: 'text-blue-400', agenda: 'text-violet-400', crm: 'text-emerald-400', cobros: 'text-amber-400',
};

const fmtRel = (iso) => {
  if (!iso) return '';
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return 'ahora';
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    return `hace ${Math.floor(diff / 86400)} días`;
  } catch { return ''; }
};

function AgentCard({ agentKey, data, onNavigate }) {
  const meta = AGENT_META[agentKey];
  const Icon = meta.icon;

  const kpis = {
    ventas: [
      { label: 'Leads totales',   value: data?.total ?? 0 },
      { label: 'Pendientes WA',   value: data?.pendientes_wa ?? 0,  alert: (data?.pendientes_wa ?? 0) > 0 },
      { label: 'Propuestas',      value: data?.propuestas ?? 0 },
      { label: 'Ganados',         value: data?.cerrado_ganado ?? 0 },
    ],
    agenda: [
      { label: 'Total citas',       value: data?.total ?? 0 },
      { label: 'Próximas',          value: data?.proximas ?? 0 },
      { label: 'Hoy',               value: data?.hoy ?? 0,             alert: (data?.hoy ?? 0) > 0 },
      { label: 'Sin recordatorio',  value: data?.sin_recordatorio ?? 0, alert: (data?.sin_recordatorio ?? 0) > 0 },
    ],
    crm: [
      { label: 'Contactos',         value: data?.total ?? 0 },
      { label: 'Calientes (≥70)',   value: data?.hot ?? 0 },
      { label: 'Sin seguimiento',   value: data?.needs_followup ?? 0, alert: (data?.needs_followup ?? 0) > 0 },
    ],
    cobros: [
      { label: 'Facturas',          value: data?.total ?? 0 },
      { label: 'Pendiente (USD)',   value: `$${(data?.pendiente_usd ?? 0).toFixed(0)}`, alert: (data?.pendiente_usd ?? 0) > 0 },
      { label: 'Vencidas',          value: data?.vencidas ?? 0,         alert: (data?.vencidas ?? 0) > 0 },
      { label: 'Recaudado (USD)',   value: `$${(data?.recaudado_usd ?? 0).toFixed(0)}` },
    ],
  }[agentKey] || [];

  const hasAlert = kpis.some(k => k.alert);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border p-5 space-y-4 cursor-pointer hover:brightness-110 transition-all ${meta.bg} ${meta.border}`}
      onClick={() => onNavigate(agentKey)}
      data-testid={`agent-card-${agentKey}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`p-2 rounded-xl ${meta.bg} border ${meta.border}`}>
            <Icon className={`w-4 h-4 ${meta.color}`} />
          </div>
          <p className={`text-sm font-bold ${meta.color}`}>{meta.label}</p>
        </div>
        {hasAlert && <AlertTriangle className="w-4 h-4 text-amber-400 animate-pulse" />}
      </div>

      {/* KPIs grid */}
      <div className="grid grid-cols-2 gap-3">
        {kpis.map(({ label, value, alert }) => (
          <div key={label}>
            <p className="text-[9px] text-text-muted uppercase tracking-wider">{label}</p>
            <p className={`text-xl font-black tabular-nums ${alert ? 'text-amber-400' : 'text-text-heading'}`}>{value}</p>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-text-muted text-right">Abrir panel →</p>
    </motion.div>
  );
}

export default function AgentsDashboard({ authFetch, onNavigateToAgent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const apiFn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFn(`${API}/agents/dashboard`);
      setData(await res.json());
    } catch { toast.error('Error al cargar el dashboard'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runQASeed = async () => {
    setSeeding(true);
    try {
      const res = await apiFn(`${API}/agents/qa-seed`, { method: 'POST' });
      const d = await res.json();
      toast.success(d.message || 'QA completado');
      await load();
    } catch { toast.error('Error en QA seed'); }
    finally { setSeeding(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-text-muted">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando dashboard…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agents-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" /> Panel de Agentes IA
          </h3>
          <p className="text-sm text-text-muted mt-0.5">Vista unificada de los 4 agentes — haz clic en cualquier tarjeta para abrir su panel.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-secondary !px-3 !py-2" data-testid="agents-dashboard-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button onClick={runQASeed} disabled={seeding} className="btn-secondary !px-4 !py-2 text-sm flex items-center gap-2" data-testid="agents-qa-seed">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            QA Recorrido completo
          </button>
        </div>
      </div>

      {/* 4 agent cards */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {['ventas', 'agenda', 'crm', 'cobros'].map((key) => (
          <AgentCard
            key={key}
            agentKey={key}
            data={data?.[key]}
            onNavigate={(k) => onNavigateToAgent?.(k)}
          />
        ))}
      </div>

      {/* Actividad reciente */}
      <div className="glass-card p-5 space-y-3">
        <h4 className="text-sm font-semibold text-text-heading flex items-center gap-2">
          <Clock className="w-4 h-4 text-text-muted" /> Actividad reciente
        </h4>
        {(data?.recent || []).length === 0 ? (
          <p className="text-xs text-text-muted text-center py-4">Sin actividad reciente. Ejecuta el agente de ventas para comenzar.</p>
        ) : (
          <div className="space-y-1">
            {(data?.recent || []).map((item, i) => {
              const Icon = RECENT_ICONS[item.type] || Zap;
              const color = RECENT_COLORS[item.type] || 'text-text-muted';
              return (
                <div key={i} className="flex items-center gap-3 py-1.5 border-b border-border-subtle last:border-0" data-testid={`recent-item-${i}`}>
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${color}`} />
                  <p className="flex-1 text-xs text-text-body truncate">{item.label}</p>
                  <p className="text-[10px] text-text-muted flex-shrink-0 tabular-nums">{fmtRel(item.at)}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* WhatsApp Perú manual notice */}
      <div className="rounded-2xl border border-amber-500/25 bg-amber-500/5 p-5 space-y-2">
        <p className="text-sm font-semibold text-amber-400 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> WhatsApp Perú (+51) — Acción manual requerida
        </p>
        <p className="text-xs text-text-muted leading-relaxed">
          El número <span className="text-text-body font-medium">+51 974 287 269</span> fue migrado a Cloud API pero requiere verificación por SMS en Meta Business Manager. Pasos:
        </p>
        <ol className="text-xs text-text-muted space-y-1 list-decimal list-inside">
          <li>Ir a <span className="text-primary">business.facebook.com</span> → WhatsApp Accounts → Gestionar</li>
          <li>Seleccionar el número +51 974 287 269 y hacer clic en "Verificar número"</li>
          <li>Elegir "SMS" como método de verificación</li>
          <li>Ingresar el código que llega al teléfono</li>
          <li>El Health Monitor detectará el estado "CONNECTED" automáticamente en el próximo ciclo (5 min)</li>
        </ol>
      </div>
    </div>
  );
}
